import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import type { ActiveTask, TaskVisibility } from '@hcc/types';
import { Button } from '../ui/Button';
import { api, ApiError } from '../../lib/api';

export const VISIBILITY_LABELS: Record<TaskVisibility, string> = {
  me: 'Inductee (always)',
  reception: 'Reception (at check-in)',
  station_it: 'IT station',
  station_ppe: 'PPE / Uniform station',
  station_tools: 'Tools station',
  station_fleet: 'Fleet station',
};

export function AssignTaskModal({
  open,
  personId,
  editing,
  onClose,
  onDone,
}: {
  open: boolean;
  personId: string;
  /** When set, the modal is in edit mode: pre-fills fields and posts
   *  TaskUpdated instead of TaskAssigned. */
  editing?: ActiveTask | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const isEdit = !!editing;
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [approvalRequired, setApprovalRequired] = useState(true);
  const [visibility, setVisibility] = useState<Set<TaskVisibility>>(
    new Set<TaskVisibility>(['me']),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-fill from `editing` when it changes (modal opens with a task).
  useEffect(() => {
    if (editing) {
      setTitle(editing.title);
      setDescription(editing.description ?? '');
      setDueAt(editing.due_at ?? '');
      setApprovalRequired(editing.approval_required);
      setVisibility(new Set<TaskVisibility>(editing.visibility));
      setError(null);
    } else if (open) {
      // Fresh create: reset to defaults each time it opens.
      setTitle('');
      setDescription('');
      setDueAt('');
      setApprovalRequired(true);
      setVisibility(new Set<TaskVisibility>(['me']));
      setError(null);
    }
  }, [editing, open]);

  if (!open) return null;

  function toggle(v: TaskVisibility) {
    const next = new Set(visibility);
    if (next.has(v)) {
      if (v === 'me') return; // 'me' always on
      next.delete(v);
    } else {
      next.add(v);
    }
    setVisibility(next);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      if (isEdit && editing) {
        // Send the full set of fields; the projector merges. Description
        // and due_at use null-to-clear semantics so an emptied form
        // wipes the value rather than silently keeping the old one.
        await api(`/api/people/${personId}/events`, {
          method: 'POST',
          body: JSON.stringify({
            event_type: 'TaskUpdated',
            payload: {
              task_id: editing.task_id,
              title: title.trim(),
              description: description.trim() ? description.trim() : null,
              due_at: dueAt || null,
              visibility: [...visibility],
              approval_required: approvalRequired,
            },
          }),
        });
        toast.success(`Updated: ${title.trim()}`);
      } else {
        await api(`/api/people/${personId}/events`, {
          method: 'POST',
          body: JSON.stringify({
            event_type: 'TaskAssigned',
            payload: {
              task_id: crypto.randomUUID(),
              title: title.trim(),
              ...(description.trim() ? { description: description.trim() } : {}),
              ...(dueAt ? { due_at: dueAt } : {}),
              visibility: [...visibility],
              approval_required: approvalRequired,
            },
          }),
        });
        toast.success(`Assigned: ${title.trim()}`);
      }
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : isEdit ? 'update failed' : 'assign failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-fg/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <form
        className="w-full max-w-md rounded-token-lg border border-line/10 bg-surface p-5 shadow-token-lg"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-base font-semibold text-fg">{isEdit ? 'Edit task' : 'Assign task'}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-token-sm p-1 text-fg-muted hover:bg-surface-2 hover:text-fg"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <label className="mt-4 block text-sm">
          <span className="font-medium text-fg">Title</span>
          <input
            type="text"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Bring DBS certificate"
            className="mt-1 block h-10 w-full rounded-token-sm border border-line-strong/20 bg-surface px-2 text-sm text-fg shadow-token-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-ring/30"
          />
        </label>

        <label className="mt-3 block text-sm">
          <span className="font-medium text-fg">Description (optional)</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="mt-1 block w-full rounded-token-sm border border-line-strong/20 bg-surface px-2 py-1.5 text-sm text-fg shadow-token-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-ring/30"
          />
        </label>

        <label className="mt-3 block text-sm">
          <span className="font-medium text-fg">Due date (optional)</span>
          <input
            type="date"
            value={dueAt}
            onChange={(e) => setDueAt(e.target.value)}
            className="mt-1 block h-10 w-full rounded-token-sm border border-line-strong/20 bg-surface px-2 text-sm text-fg shadow-token-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-ring/30"
          />
        </label>

        <label className="mt-3 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={approvalRequired}
            onChange={(e) => setApprovalRequired(e.target.checked)}
            className="h-4 w-4 rounded border-line-strong/30 accent-accent"
          />
          <span className="text-fg">Requires HR approval after submission</span>
        </label>

        <fieldset className="mt-3">
          <legend className="text-xs font-semibold uppercase tracking-wider text-fg-muted">
            Reminder also shows on
          </legend>
          <div className="mt-2 space-y-1.5">
            {(Object.keys(VISIBILITY_LABELS) as TaskVisibility[]).map((v) => (
              <label key={v} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={visibility.has(v)}
                  disabled={v === 'me'}
                  onChange={() => toggle(v)}
                  className="h-4 w-4 rounded border-line-strong/20"
                />
                <span className={v === 'me' ? 'text-fg-muted' : ''}>
                  {VISIBILITY_LABELS[v]}
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        {error ? (
          <div className="mt-3 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
            {error}
          </div>
        ) : null}

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="md" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" size="md" type="submit" loading={saving}>
            {isEdit ? 'Save' : 'Assign'}
          </Button>
        </div>
      </form>
    </div>
  );
}
